import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Mail, Phone, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, REGIONS, SERVICE_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", company: "", region: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.region || !form.service || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received. Our engineering team will respond shortly.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0A111D] border-white/15 text-[#F8FAFC] placeholder:text-[#5b6b80] font-mono-tech text-sm h-12 rounded-none focus-visible:ring-[#FF5A00] focus-visible:ring-2 focus-visible:border-[#FF5A00]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A111D] py-24 lg:py-36 border-t border-white/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: intro + details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#FF5A00]">
            Request for Quotation
          </span>
          <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#F8FAFC]">
            Start an engineering conversation.
          </h2>
          <p className="mt-6 font-mono-tech text-sm leading-relaxed text-[#94A3B8] max-w-md">
            Tell us about your platform, fleet or wind asset. We respond to every enquiry
            with a scoped technical assessment.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <span className="grid place-items-center h-11 w-11 border border-white/15 group-hover:border-[#FF5A00] transition-colors">
                <Mail className="h-4 w-4 text-[#FF5A00]" />
              </span>
              <span className="font-mono-tech text-sm text-[#94A3B8] group-hover:text-[#F8FAFC] transition-colors">
                {COMPANY.email}
              </span>
            </a>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="grid place-items-center h-11 w-11 border border-white/15 group-hover:border-[#FF5A00] transition-colors">
                <Phone className="h-4 w-4 text-[#FF5A00]" />
              </span>
              <span className="font-mono-tech text-sm text-[#94A3B8] group-hover:text-[#F8FAFC] transition-colors">
                {COMPANY.phone}
              </span>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <span className="grid place-items-center h-11 w-11 border border-white/15 shrink-0">
                <MapPin className="h-4 w-4 text-[#FF5A00]" />
              </span>
              <span className="font-mono-tech text-sm text-[#94A3B8] max-w-xs leading-relaxed">
                {COMPANY.address}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 bg-[#050B14] border border-white/10 p-8 lg:p-12"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                Full Name *
              </label>
              <Input
                data-testid="input-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Jane Mariner"
                className={`mt-2 ${inputCls}`}
              />
            </div>
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                Work Email *
              </label>
              <Input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="jane@operator.com"
                className={`mt-2 ${inputCls}`}
              />
            </div>
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                Company
              </label>
              <Input
                data-testid="input-company"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="Maritime Operator Ltd"
                className={`mt-2 ${inputCls}`}
              />
            </div>
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                Region *
              </label>
              <Select value={form.region} onValueChange={(v) => set("region", v)}>
                <SelectTrigger
                  data-testid="select-region"
                  className={`mt-2 ${inputCls}`}
                >
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A111D] border-white/15 text-[#F8FAFC] font-mono-tech">
                  {REGIONS.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`region-${r}`}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6">
            <label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              Service Required *
            </label>
            <Select value={form.service} onValueChange={(v) => set("service", v)}>
              <SelectTrigger data-testid="select-service" className={`mt-2 ${inputCls}`}>
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A111D] border-white/15 text-[#F8FAFC] font-mono-tech">
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`service-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-6">
            <label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              Project Details *
            </label>
            <Textarea
              data-testid="input-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Describe your asset, scope and timeline…"
              rows={5}
              className={`mt-2 bg-[#0A111D] border-white/15 text-[#F8FAFC] placeholder:text-[#5b6b80] font-mono-tech text-sm rounded-none focus-visible:ring-[#FF5A00] focus-visible:ring-2 focus-visible:border-[#FF5A00] resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="rfq-submit-button"
            className="mt-8 w-full sm:w-auto group inline-flex items-center justify-center gap-3 bg-[#FF5A00] text-white font-mono-tech text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-[#F8FAFC] hover:text-[#050B14] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting
              </>
            ) : (
              <>
                Submit Inquiry
                <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
